import {SharedProps} from '../sharedProps';

export type EAutocompleteOption = {
  value: string;
  displayText: string;
  subtext?: string | React.ReactElement;
  titleText?: string;
  Icon?: React.ReactElement;
  endAdornment?: React.ReactElement;
  /** Optional property if the text that should be displayed in the dropdown differs from the displayText  */
  dropdownDisplayText?: string | React.ReactElement;
  disabled?: boolean;
  /**
   * Renders this option inside the category with this name.
   */
  categoryName?: string;
  /**
   * Mark if it is not a real option but a placeholder e.g. to decide to create contacts
   */
  isPlaceholderOption?: boolean;
  /** DO NOT USE THIS PROP. This prop is only for internal use when multiple is set to true */
  selected?: boolean;
};

export type EAutocompleteCategoryDefinitionProps = {
  /**
   * The name of the category which can be referenced in EAutocompleteOption::categoryName.
   *
   * If the name is missing it's the main category.
   */
  name?: string;

  /**
   * The top label of the category displayed in a smaller font.
   */
  topLabel?: string;

  /**
   * The main label of the category which is displayed inside the autocomplete.
   */
  mainLabel?: string;
};

export type EAutocompleteProps = SharedProps & {
  value?: string;
  options: EAutocompleteOption[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent) => void;
  allowCustomInput?: boolean;
  takeHighlightedOptionOnBlur?: boolean;
  dynamicDropdownWidth?: boolean;
  isLoading?: boolean;
  onSearchQueryChange?: (query: string) => void;
  /** placeholder value that gets displayed if nothing is selected */
  placeholder?: string;

  /* lazy loading props */
  /** set this property to true if no filtering of the options should be performed (for example if the filtering is done outside the component) */
  noOptionFiltering?: boolean;
  /** tells the component if there are more elements to be loaded before */
  hasMoreBefore?: boolean;
  /** tells the component if there are more elements to be loaded after */
  hasMoreAfter?: boolean;
  /** function called when more elements need to be loaded before */
  onLoadMoreBefore?: () => Promise<void>;
  /** function called when more elements need to be loaded after */
  onLoadMoreAfter?: () => Promise<void>;
  openOnFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  /** boolean that disables the popover to open on autoFoucs - default false */
  disableOpenOnAutofocus?: boolean;
  /**
   * A list of categories which are supported by this autocomplete.
   *
   * Options can be displayed in a certain category if a "category" is provided.
   */
  categories?: EAutocompleteCategoryDefinitionProps[];
  /**
   * Decides if the clear button in the autocomplete is focusable via tab
   */
  tabfocusableClear?: boolean;
  /**
   * allowEnterSubmit - allows an enter to submit a form by not intercepting enter keys in the field
   */
  allowEnterSubmit?: boolean;
  clearOnSelection?: boolean;
  /**
   * Hides the clear button if set to true
   */
  hideClearButton?: boolean;

  /**
   * aligns the items in the dropdown
   */
  align?: 'right' | 'left';

  moreOptionPadding?: boolean;

  /**
   * Don't clear search query on change
   */
  disableClearSearchQueryOnChange?: boolean;

  /**
   * Don't clear search query on blur
   */
  disableClearSearchQueryOnBlur?: boolean;

  /**
   * Option to disable direct selection of a single option on a required field.
   */
  disableSingleOptionSelection?: boolean;

  /**
   * Shows settings icon button and triggers on click
   * @param e
   */
  onFilterClick?: (e: React.MouseEvent) => void;

  optionFilterInfo?: string | React.ReactElement;

  /**
   * is the value required, e.g. if only one option is available it will be preselected
   */
  required?: boolean;

  multiple?: boolean;
};

export enum OnChangeEventType {
  Change = 'change',
  ClearOnSelection = 'clearOnSelection',
}

// This is the category used for all options when no other categories are defined.
// Only options without a `category` set are displayed inside this category.
// The main category, however, is not automatically added to the list of categories
// provided by the user of the autocomplete.
export const MAIN_CATEGORY: EAutocompleteCategoryDefinitionProps = {};

export type CategoryWithOptions = {
  options: EAutocompleteOption[];
} & EAutocompleteCategoryDefinitionProps;

export const isAutocompleteOption = (
  item: EAutocompleteOption | string | null
): item is EAutocompleteOption =>
  item !== null && !(typeof item === 'string' || item instanceof String);

export type GetOptionPropsType = ({
  option,
  index,
}: {
  option: EAutocompleteOption;
  index: number;
}) => React.HTMLAttributes<HTMLLIElement>;

export type EAutocompleteCategoriesProps = {
  categories: CategoryWithOptions[];
  getOptionProps: GetOptionPropsType;
  multiple?: boolean;
  onCheckboxClick: (option: EAutocompleteOption) => void;
};
